import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Group } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	resetFlattenDraft,
	setDeleted,
	setFlattenLoading,
	setSelectedShapeId,
	ShapeId,
	updateFlattenPolygonPoints,
} from 'store/slices/TreeSlice';
import { selectFlattenAreaState, selectIsFlattenAreaMode, selectFlattenShape } from 'store/slices/TreeSlice/selector';
import ActiveHandles from './ActiveHandles';
import FlattenAreaCircleTool from './FlattenAreaCircleTool';
import FlattenAreaFreeDrawTool from './FlattenAreaFreeDrawTool';
import FlattenAreaLoadingOutline from './FlattenAreaLoadingOutline';
import FlattenAreaPolygonTool from './FlattenAreaPolygonTool';
import FlattenAreaPolygon, { POLYGON_DASH, POLYGON_STROKE, POLYGON_STROKE_WIDTH } from './FlattenAreaPolygon';
import { findFlattenAreaAtPoint, getImagePointFromEvent } from './flattenAreaToolUtils';
import { usePreviewFlatten } from 'hooks/api/editDsm/useFlattenPreview';

const CLOSE_DISTANCE_PX = 5;

export default function FlattenAreaDrawer() {
	const dispatch = useDispatch();
	const { konvaRef } = useRefs();
	const baseImageScale = useKonvaImageScale();

	const { treeEditingMode, deleteMode } = useSelector((state: RootState) => state.treeSlice.data);
	const { editModeEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { activePolygon, flattenAreaPolygons, flattenAreaShapeById } = useSelector(selectFlattenAreaState);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	const flattenShape = useSelector(selectFlattenShape);
	const draftPointsCount = useSelector((state: RootState) => state.treeSlice.data.draftFlatten.points.length);
	const loadingFlattenIds = useSelector((state: RootState) => state.treeSlice.data.loadingFlattenIds);

	const [hoveredPolygonId, setHoveredPolygonId] = useState<ShapeId | null>(null);

	const lineRefs = useRef<Map<ShapeId, Konva.Line>>(new Map());

	const { mutate: runFringeDetection } = usePreviewFlatten();
	const flattenAreaAdded = useSelector((state: RootState) => state.treeSlice.data.flattenArea.added);
	const sentIdsRef = useRef<Set<string>>(new Set());

	// ── Fringe detection — fires once per draw completion, covers 2D and 3D ──
	useEffect(() => {
		for (const id of Object.keys(flattenAreaAdded)) {
			if (sentIdsRef.current.has(id)) continue;
			const polygon = flattenAreaPolygons.find(p => String(p.id) === id);
			if (!polygon) continue;
			sentIdsRef.current.add(id);

			dispatch(setFlattenLoading({ id, loading: true }));
			runFringeDetection({ coords: polygon.coords, id });
		}
	}, [flattenAreaAdded, runFringeDetection, dispatch, flattenAreaPolygons]);

	const canEdit = editModeEnabled && treeEditingMode && isFlattenAreaMode;
	const canDelete = editModeEnabled && treeEditingMode && deleteMode && !isFlattenAreaMode;
	const drawingInProgress = canEdit && draftPointsCount > 0;
	const canHoverPolygons = canDelete || (canEdit && !drawingInProgress);

	const setStageCursor = useCallback((cursor: string) => {
		const stage = konvaRef?.current;
		if (!stage) return;
		stage.container().style.cursor = cursor;
	}, [konvaRef]);

	const getBaseCursor = useCallback(() => {
		if (!editModeEnabled) return 'default';
		if (canEdit) return 'crosshair';
		if (canDelete) return 'default';
		return 'default';
	}, [canDelete, canEdit, editModeEnabled]);

	const setActivePolygonId = useCallback((id: ShapeId) => {
		dispatch(setSelectedShapeId(id));
	}, [dispatch]);

	// `points`, if given, is the drag's own buffer — safer than Konva's line (can reset mid-drag).
	const commitPolygonPoints = useCallback((id: ShapeId, points?: number[]) => {
		const nextPoints = points ?? lineRefs.current.get(id)?.points().slice();
		if (!nextPoints) return;
		dispatch(updateFlattenPolygonPoints({ id, points: nextPoints }));
	}, [dispatch]);

	useEffect(() => {
		if (canEdit) return;
		if (draftPointsCount > 0) dispatch(resetFlattenDraft());
	}, [canEdit, dispatch, draftPointsCount]);

	useEffect(() => {
		if (!canEdit && !canDelete) return;
		setStageCursor(getBaseCursor());
	}, [canDelete, canEdit, getBaseCursor, setStageCursor]);

	useEffect(() => {
		if (canHoverPolygons) return;
		setHoveredPolygonId(null);
	}, [canHoverPolygons]);

	// Fallback hover sync: recomputes the real target every move (Konva's enter/leave can miss a shape's node).
	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage || !canHoverPolygons) return;

		const syncHoverFromPointer = (e: Konva.KonvaEventObject<PointerEvent>) => {
			const point = getImagePointFromEvent(e, baseImageScale);
			if (!point) return;
			const existingShape = findFlattenAreaAtPoint(point, flattenAreaPolygons);
			const nextHoveredId = existingShape && !loadingFlattenIds[String(existingShape.id)] ? existingShape.id : null;
			setHoveredPolygonId(nextHoveredId);
			setStageCursor(nextHoveredId !== null ? 'pointer' : getBaseCursor());
		};

		stage.on('pointermove', syncHoverFromPointer);
		return () => { stage.off('pointermove', syncHoverFromPointer); };
	}, [konvaRef, canHoverPolygons, baseImageScale, flattenAreaPolygons, loadingFlattenIds, getBaseCursor, setStageCursor]);

	if (!treeEditingMode ) return null;

	return (
		<Group name="flatten-area-drawer" scale={baseImageScale} listening={true}>
			{canEdit && flattenShape === 'polygon' && <FlattenAreaPolygonTool />}
			{canEdit && flattenShape === 'circle' && <FlattenAreaCircleTool />}
			{canEdit && flattenShape === 'free' && <FlattenAreaFreeDrawTool />}

			{flattenAreaPolygons.map((polygon) => {
				const points = polygon.coords.flat();
				if (points.length < 6) return null;
				const isActive = activePolygon?.id === polygon.id;
				const isLoading = !!loadingFlattenIds[String(polygon.id)];
				return (
					<Group
						key={polygon.id}
						// draggable={isActive}
						onPointerDown={(e) => {
							// Still loading — swallow the click (no select/delete, no fall-through to draw).
							if (isLoading) {
								e.cancelBubble = true;
								return;
							}
							if (canDelete) {
								e.cancelBubble = true;
								dispatch(setDeleted(polygon.id));
								return;
							}
							if (!canEdit) return;
							e.cancelBubble = true;
							if (drawingInProgress) return;
							setActivePolygonId(polygon.id);
						}}
						onPointerEnter={() => {
							if (!canHoverPolygons) return;
							setHoveredPolygonId(polygon.id);
							if (canDelete) {
								setStageCursor('pointer');
								return;
							}
							if (canEdit) setStageCursor('pointer');
						}}
						onPointerLeave={() => {
							if (!canHoverPolygons) return;
							setHoveredPolygonId((prev) => (prev === polygon.id ? null : prev));
							if (canDelete || canEdit) setStageCursor(getBaseCursor());
						}}
					>
						<FlattenAreaPolygon
							ref={(node) => {
								if (node) lineRefs.current.set(polygon.id, node);
								else lineRefs.current.delete(polygon.id);
							}}
							polygonId={polygon.id}
							points={points}
							isActive={isActive}
							isHovered={canHoverPolygons && hoveredPolygonId === polygon.id}
							listening={canEdit || canDelete}
						/>
						{isLoading && <FlattenAreaLoadingOutline points={points} />}
						{isActive && (
							<ActiveHandles polygon={polygon}
								shape={flattenAreaShapeById[String(polygon.id)] ?? 'polygon'}
								lineRefs={lineRefs}
								onCommit={commitPolygonPoints}
								onSelect={setActivePolygonId}
							/>
						)}
					</Group>
				);
			})}
		</Group>
	);
}
